import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { isAdmin } from '@app/core/misc/isAdmin';
import { ControlValueAccessorFn } from '@app/core/state/angularTypes';
import { UserType } from '@app/core/state/types';
import { UserSelectors } from '@app/core/state/users/users.selectors';
import { BystSharedModule } from '@app/shared/byst-shared.module';
import { TableColumn } from '@app/shared/components/table/interfaces/table-column';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TranslocoModule } from '@jsverse/transloco';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { TranslatableStringType } from 'src/app/core/i18n/types';
import { BlFrontendTextInputComponent } from 'src/app/core/wrappers/bl-frontend-text-input.component';

interface TeamUserListComponentTranslation {
  name: TranslatableStringType;
  role: TranslatableStringType;
}

@Component({
  selector: 'app-team-user-list',
  templateUrl: './team-user-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BystSharedModule,
    TranslocoModule,
    BlFrontendTextInputComponent,
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TeamUserListComponent), multi: true },
    getTranslationProvider('TeamUserListComponent'),
  ],
})
export class TeamUserListComponent
  extends TranslatableComponent<TeamUserListComponentTranslation>
  implements ControlValueAccessor
{
  users$: Observable<UserType[]>;
  columns$: Observable<TableColumn[]>;

  selectedUsers: UserType[];
  showOnlySelectedRows = false;

  onChange: ControlValueAccessorFn = () => {};
  onTouched: ControlValueAccessorFn = () => {};

  private showOnlySelectedRowsBS = new BehaviorSubject<boolean>(false);

  get filterFields$() {
    return this.columns$.pipe(map((columns) => columns.map((column) => column.field)));
  }

  get sortField$() {
    return this.columns$.pipe(map((columns) => columns[0]?.field));
  }

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.columns$ = this.getTranslatedColumns();
    this.users$ = this.getFilteredUsers();
  }

  writeValue(obj: UserType[]): void {
    this.selectedUsers = obj;
    this.changeDetectorRef.markForCheck();
    // reset checkbox and filter
    this.showOnlySelectedRowsBS.next(false);
    this.showOnlySelectedRows = false;
  }

  registerOnChange(fn: ControlValueAccessorFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: ControlValueAccessorFn): void {
    this.onTouched = fn;
  }

  refilterUsers(event?: boolean) {
    const showOnlySelectedRows = typeof event === 'boolean' ? event : this.showOnlySelectedRowsBS.value;
    this.showOnlySelectedRowsBS.next(showOnlySelectedRows);
  }

  isAdmin(user: UserType) {
    return isAdmin(user);
  }

  getRoleLabel(user: UserType) {
    return isAdmin(user) ? 'Administratör' : 'Medarbetare';
  }

  private getTranslatedColumns(): Observable<TableColumn[]> {
    return this.translationsLoaded$.pipe(
      map((translations) => [
        { field: 'name', header: translations?.name || '' },
        { field: 'roleLabel', header: translations?.role || '' },
      ]),
    );
  }

  private getFilteredUsers(): Observable<UserType[]> {
    return this.store.select(UserSelectors.activeUsersWithoutAll).pipe(
      switchMap((users) =>
        this.showOnlySelectedRowsBS.pipe(
          map((showOnlySelectedRows) => {
            if (!showOnlySelectedRows) return users;
            const filteredUserIds = this.selectedUsers.map((user) => user.id);
            return users.filter((user) => filteredUserIds.includes(user.id));
          }),
        ),
      ),
    );
  }
}
